import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, BookOpen, Activity, Users, PenLine, Clock, Mic, Code2, ArrowRight, User, Zap, Search, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ServiceCard } from '@/components/ServiceCard';

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const INDIGO = '#8899ff';

// ── DATA ──

const generalCards = {
  free: [
    {
      id: 'conversation', icon: MessageSquare, title: 'Start a Conversation with R. Dale',
      badges: [{ label: 'Complimentary', type: 'green' }, { label: 'Recommended Start', type: 'gold-filled' }],
      price: 'Free', duration: '30 minutes',
      description: 'A complimentary introductory call to get to know each other, discuss what you\'re working on, and explore whether there\'s a fit for further conversation or collaboration.',
      note: 'This is a starting point, not an extended consulting session.',
      ctaLabel: 'Book Now', ctaLink: 'mailto:robert@rdbrunet.com?subject=Intro Call Request', featured: true,
    },
    {
      id: 'book-dev', icon: BookOpen, title: 'Book Development & Publishing Support',
      badges: [{ label: 'Complimentary', type: 'green' }],
      price: 'Free', duration: '30 minutes',
      description: 'A complimentary conversation for authors, aspiring authors, and creators who want to discuss a book idea, manuscript development, or publishing direction.',
      ctaLabel: 'Schedule', ctaLink: 'mailto:robert@rdbrunet.com?subject=Book Development Inquiry',
    },
  ],
  paid: [
    {
      id: 'strategy', icon: Activity, title: 'Private Strategy Session',
      badges: [{ label: '$225/hour', type: 'gold' }],
      price: '$225 / hour', duration: '60 minutes',
      description: 'A focused working session for deeper discussion around strategy, business direction, technology, AI, publishing, positioning, or next steps.',
      note: 'Best for those ready to move beyond an introductory conversation.',
      ctaLabel: 'Schedule', ctaLink: 'mailto:robert@rdbrunet.com?subject=Private Strategy Session',
    },
    {
      id: 'mentoring', icon: Users, title: 'Entrepreneur Support & Mentoring',
      badges: [{ label: '$125/hour', type: 'gold' }],
      price: '$125 / hour', duration: '60 minutes',
      description: 'A practical mentoring session for entrepreneurs seeking grounded support, perspective, and guidance around business decisions, growth, direction, and momentum.',
      ctaLabel: 'Schedule', ctaLink: 'mailto:robert@rdbrunet.com?subject=Entrepreneur Mentoring Request',
    },
    {
      id: 'speaking-inquiry', icon: PenLine, title: 'Speaking / Panel / Collaboration Inquiry',
      badges: [{ label: 'Custom Pricing', type: 'gold' }],
      price: 'Custom', duration: '15–30 min inquiry',
      description: 'For event organizers, podcast hosts, panel invitations, interviews, and meaningful collaboration opportunities.',
      ctaLabel: 'Reach Out', ctaLink: 'mailto:robert@rdbrunet.com?subject=Speaking / Panel Inquiry',
    },
  ],
};

const assessmentCards = [
  {
    id: 'assessment', icon: Search, title: 'AI Opportunity Assessment',
    badges: [{ label: 'AI Whisperer', type: 'indigo' }],
    subheading: 'Evaluate existing workflows and identify automation opportunities.',
    bullets: [
      'Comprehensive review of current operational processes',
      'Identification of high-impact automation candidates',
      'Technology readiness evaluation',
      'Prioritized roadmap of AI integration opportunities',
    ],
    ctaLabel: 'Inquire', ctaLink: 'mailto:robert@rdbrunet.com?subject=AI Opportunity Assessment',
  },
  {
    id: 'workflow', icon: Settings, title: 'Workflow Automation Architecture',
    badges: [{ label: 'AI Whisperer', type: 'indigo' }],
    subheading: 'Design practical AI-assisted systems to improve productivity.',
    bullets: [
      'Custom workflow design using modern AI tools',
      'Integration planning with existing systems and teams',
      'Implementation strategy with measurable milestones',
      'Staff training and change management guidance',
    ],
    ctaLabel: 'Inquire', ctaLink: 'mailto:robert@rdbrunet.com?subject=Workflow Automation Architecture',
  },
];

const aiCards = {
  programs: [
    {
      id: 'clarity', icon: Clock, title: 'AI Clarity Session',
      badges: [{ label: 'AI Whisperer', type: 'indigo' }, { label: 'Best Entry Point', type: 'gold-filled' }],
      price: '$499 flat rate', duration: '2 hours', groupSize: '1–3 people',
      description: 'A focused working session to understand your business and identify practical, immediate ways to integrate AI — without the hype, without the overwhelm.',
      includes: ['Guided discovery discussion — you lead the thinking', 'Session recording + full transcript', 'Structured follow-up summary with specific next steps'],
      ctaLabel: 'Book This Session', ctaLink: 'mailto:robert@rdbrunet.com?subject=AI Clarity Session Inquiry', featured: true,
    },
    {
      id: 'working', icon: Users, title: 'AI Working Session',
      badges: [{ label: 'AI Whisperer', type: 'indigo' }],
      price: '$1,000–$1,200', duration: '3 hours', groupSize: '6–8 people',
      description: 'A hands-on, interactive small group session designed to shift thinking and apply AI to real business scenarios. For teams, peer groups, and organizations.',
      includes: ['What AI is / isn\'t — a genuine mindset reset', 'Real-world use cases with live demonstrations', 'Group application to your actual business challenges', 'Participants leave with actionable, specific insight'],
      ctaLabel: 'Inquire', ctaLink: 'mailto:robert@rdbrunet.com?subject=AI Working Session Inquiry',
    },
  ],
  ongoing: [
    {
      id: 'consulting', icon: Code2, title: 'One-on-One AI Consulting',
      badges: [{ label: 'AI Whisperer', type: 'indigo' }, { label: '$225/hour', type: 'gold' }],
      price: '$225 / hour',
      description: 'Direct, personalized support for applying AI to specific business challenges. Ideal for follow-up from sessions, deeper workflow dives, and ongoing implementation guidance.',
      includes: ['Workflow analysis and AI integration planning', 'Implementation guidance with real tools', 'Focused problem-solving with immediate application'],
      ctaLabel: 'Schedule', ctaLink: 'mailto:robert@rdbrunet.com?subject=AI Consulting Inquiry',
    },
    {
      id: 'ai-speaking', icon: Mic, title: 'AI Whisperer Speaking Engagement',
      badges: [{ label: 'AI Whisperer', type: 'indigo' }, { label: 'Custom', type: 'gold' }],
      price: 'Custom per event', duration: '20 min – full day',
      description: 'A high-impact talk that helps audiences rethink AI and understand how to use it without overwhelm. Available in multiple formats for any stage or event type.',
      includes: ['20-minute overview talk', '45–60 minute keynote', 'Extended workshop-style sessions'],
      ctaLabel: 'Inquire', ctaLink: 'mailto:robert@rdbrunet.com?subject=AI Whisperer Speaking Inquiry',
    },
  ],
};

const funnel = [
  { num: 1, label: 'Speak', sub: 'Create awareness' },
  { num: 2, label: 'Engage', sub: 'Clarity sessions' },
  { num: 3, label: 'Convert', sub: 'Working sessions' },
  { num: 4, label: 'Deliver', sub: '1:1 consulting' },
  { num: 5, label: 'Repeat', sub: 'Ongoing momentum' },
];

const pricingRows = [
  { dot: 'bg-emerald-400', name: 'Intro Conversation & Book Development', price: 'Complimentary' },
  { dot: 'bg-primary', name: 'Private Strategy Sessions', price: '$225 / hour' },
  { dot: 'bg-primary', name: 'Entrepreneur Support & Mentoring', price: '$125 / hour' },
  { dot: 'bg-[#8899ff]', name: 'AI Opportunity Assessment', price: 'Contact for pricing' },
  { dot: 'bg-[#8899ff]', name: 'Workflow Automation Architecture', price: 'Contact for pricing' },
  { dot: 'bg-[#8899ff]', name: 'AI Clarity Session (1–3 people)', price: '$499 flat' },
  { dot: 'bg-[#8899ff]', name: 'AI Working Session (6–8 people)', price: '$1,000–$1,200' },
  { dot: 'bg-[#8899ff]', name: 'AI Consulting (ongoing)', price: '$225 / hour' },
  { dot: 'bg-[#8899ff]', name: 'Speaking / Events', price: 'Custom' },
];

// ── STICKY TWO-TRACK NAV ──

function TrackNav({ stripRef }) {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-65px 0px 0px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [stripRef, setStuck]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const panels = [
    { id: 'general', icon: User, label: 'R. Dale Brunet', sub: 'Strategy · Publishing · Mentoring' },
    { id: 'ai-whisperer', icon: Zap, label: 'The AI Whisperer', sub: 'AI Clarity · Workshops · Implementation' },
  ];

  const inner = (
    <div className="max-w-[1100px] mx-auto px-6 md:px-12">
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        {panels.map((p) => (
          <button
            key={p.id}
            onClick={() => scrollTo(p.id)}
            className="group flex items-center gap-3 md:gap-4 p-4 md:p-5 rounded-xl border border-border/30 bg-card/80 hover:border-primary/50 hover:bg-card transition-all text-left"
            data-testid={`track-${p.id}`}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center flex-shrink-0 transition-colors">
              <p.icon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            </div>
            <div>
              <span className="text-sm md:text-base font-bold text-white block">{p.label}</span>
              <span className="text-xs md:text-sm text-slate-400">{p.sub}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* In-flow element (observed for sticky trigger) */}
      <div ref={stripRef} className="py-4 md:py-5" data-testid="track-strip">
        {inner}
      </div>
      {/* Sticky clone */}
      {stuck && (
        <div className="fixed top-[64px] left-0 right-0 z-40 py-3 glass-effect border-b border-border/30" data-testid="track-strip-sticky">
          {inner}
        </div>
      )}
    </>
  );
}

// ── PAGE ──

export default function SchedulePage() {
  const stripRef = useRef(null);

  useEffect(() => {
    document.title = 'Work With R. Dale Brunet · rdbrunet.com';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Book a strategy session, AI clarity workshop, publishing support, mentoring, or speaking engagement with R. Dale Brunet — Technology Architect and AI Whisperer.');
    return () => { document.title = 'R. Dale Brunet | Technology Architect, AI Strategist, Founder, Author'; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="pt-20" data-testid="schedule-page">

      {/* ── HERO ── */}
      <section className="py-10 md:py-14" data-testid="schedule-hero">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.span
            className="text-2xl sm:text-3xl lg:text-4xl text-primary font-bold uppercase tracking-[0.15em] mb-4 block"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            Work With R. Dale Brunet
          </motion.span>
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1]"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            data-testid="schedule-headline"
          >
            Let's Build Something <em className="text-primary italic">Real</em> Together
          </motion.h1>
          <motion.p
            className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto mb-6"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          >
            Whether you're exploring AI, developing a book, shaping your business strategy, or looking for a speaker who actually knows what they're talking about — there's a right conversation for where you are right now.
          </motion.p>
          <motion.div
            className="flex flex-wrap justify-center gap-4"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          >
            <Button
              size="lg"
              className="bg-primary text-white hover:text-white hover:bg-primary/80 px-8"
              onClick={() => document.getElementById('general')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="explore-offerings-btn"
            >
              Explore Offerings <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button asChild size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800 px-8">
              <a href="/contact" data-testid="email-directly-btn">Contact Now</a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ── TWO-TRACK NAV ── */}
      <TrackNav stripRef={stripRef} />

      {/* ── SECTION 1: R. DALE BRUNET SERVICES ── */}
      <section id="general" className="py-8 md:py-10 bg-slate-900/50" data-testid="general-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <motion.div {...fade} className="mb-6">
            <span className="text-sm text-primary font-semibold uppercase tracking-[0.2em] mb-2 block">R. Dale Brunet</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">General Conversations & Strategy</h2>
            <p className="text-slate-400 max-w-2xl">Introductory, business strategy, publishing support, and entrepreneurial mentoring — the range of ways we can work together directly.</p>
          </motion.div>

          <motion.div {...fade}>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Complimentary</span>
            <hr className="border-border/30 mt-2 mb-4" />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {generalCards.free.map((c) => <ServiceCard key={c.id} card={c} featured={c.featured} />)}
          </div>

          <motion.div {...fade}>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Paid Engagements</span>
            <hr className="border-border/30 mt-2 mb-4" />
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {generalCards.paid.map((c) => <ServiceCard key={c.id} card={c} />)}
          </div>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-6 md:px-12"><hr className="border-border/20" /></div>

      {/* ── SECTION 2: AI WHISPERER ── */}
      <section id="ai-whisperer" className="py-8 md:py-10" data-testid="ai-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <motion.div {...fade} className="mb-4">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] mb-2 block" style={{ color: INDIGO }}>The AI Whisperer</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">AI Clarity, Training & Implementation</h2>
            <p className="text-slate-400 max-w-2xl">Structured programs designed to shift how you think about AI — and give you the tools to actually use it in your business.</p>
          </motion.div>

          {/* Approach quote */}
          <motion.div
            className="p-5 rounded-xl border-l-4 border-primary bg-card/30 mb-6"
            {...fade}
            data-testid="ai-approach-quote"
          >
            <p className="text-slate-300 leading-relaxed text-sm">
              My approach is grounded in decades of designing real-world operational systems — from financial reporting platforms and risk management tools to enterprise tracking and workflow automation used in high-stakes environments. This isn't theoretical. It's applied experience — now extended into AI-driven systems that improve how organizations actually operate.
            </p>
          </motion.div>

          {/* Assessments & Architecture */}
          <motion.div {...fade}>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Assessments & Architecture</span>
            <hr className="border-border/30 mt-2 mb-4" />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {assessmentCards.map((c) => <ServiceCard key={c.id} card={c} accentColor="indigo" />)}
          </div>

          {/* Structured Programs */}
          <motion.div {...fade}>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Structured Programs</span>
            <hr className="border-border/30 mt-2 mb-4" />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {aiCards.programs.map((c) => <ServiceCard key={c.id} card={c} featured={c.featured} accentColor="indigo" />)}
          </div>

          {/* Ongoing & Events */}
          <motion.div {...fade}>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Ongoing & Events</span>
            <hr className="border-border/30 mt-2 mb-4" />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {aiCards.ongoing.map((c) => <ServiceCard key={c.id} card={c} accentColor="indigo" />)}
          </div>

          {/* Funnel */}
          <motion.div {...fade} className="text-center">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4 block">The AI Whisperer Model</span>
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-0">
              {funnel.map((step, i) => (
                <div key={step.num} className="flex items-center gap-3 md:gap-0">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-primary font-bold text-sm">
                      {step.num}
                    </div>
                    <span className="text-sm font-semibold text-white">{step.label}</span>
                    <span className="text-xs text-slate-500">{step.sub}</span>
                  </div>
                  {i < funnel.length - 1 && <ArrowRight className="w-5 h-5 text-slate-600 mx-4 hidden md:block" />}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-6 md:px-12"><hr className="border-border/20" /></div>

      {/* ── SECTION 3: TRANSPARENCY ── */}
      <section id="transparency" className="py-8 md:py-10 bg-slate-900/50" data-testid="transparency-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fade} className="text-center mb-5">
            <span className="text-sm text-primary font-semibold uppercase tracking-[0.2em] mb-2 block">Transparency</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">How This Works</h2>
          </motion.div>

          <motion.div className="bg-card border border-border/30 rounded-xl p-6 md:p-8" {...fade} data-testid="how-it-works-card">
            <p className="text-slate-300 leading-relaxed mb-5">
              The complimentary sessions are genuine starting points — not sales calls disguised as conversations. They exist to determine fit. If it makes sense to continue working together, ongoing engagements are billed at the rates below. Everything is clear, upfront, and respectful of your time.
            </p>
            <div className="space-y-2 mb-5">
              {pricingRows.map((row, i) => (
                <div key={row.name} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/40 border border-border/20">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${row.dot}`} />
                  <span className="text-sm text-white font-medium flex-1">{row.name}</span>
                  <span className="text-sm text-slate-300 font-semibold">{row.price}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 italic leading-relaxed">
              This page is intended for thoughtful, productive conversations. If you're looking for quick technical support or highly transactional troubleshooting, this may not be the best fit. The goal here is meaningful dialogue, useful perspective, and clear next steps.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CONTACT STRIP ── */}
      <div className="border-t border-b border-border/30 py-4" data-testid="contact-strip">
        <p className="text-center text-sm text-slate-400 px-6">
          If you'd prefer to reach out directly before scheduling —{' '}
          <a href="mailto:robert@rdbrunet.com" className="text-primary font-medium hover:underline underline-offset-4">
            robert@rdbrunet.com
          </a>
        </p>
      </div>

    </main>
  );
}
